import {
  BookingStatusFilterEnum,
  BookingWithDetail,
  CreateBooking,
  Credit,
  CreditTxWithMeta
} from "@garther/interfaces";
import { create } from "zustand";

import { bookingApiClient, creditApiClient } from "@/libs/api";

interface CreditState {
  // State
  userCredits: Credit | null;
  transactions: CreditTxWithMeta[];
  bookings: BookingWithDetail[];
  loading: boolean;
  error: string | null;
  creditPrice: number;

  // Actions
  loadUserCredits: (userId: string) => Promise<void>;
  loadTransactions: (userId: string) => Promise<void>;
  loadBookings: (userId: string) => Promise<void>;
  bookDinner: (data: CreateBooking) => Promise<BookingWithDetail | null>;
  refreshCredits: (userId: string) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  userCredits: null,
  transactions: [],
  bookings: [],
  loading: false,
  error: null,
  creditPrice: 50000
};

export const useCreditStore = create<CreditState>((set) => ({
  ...initialState,

  loadUserCredits: async (_userId: string) => {
    set({ loading: true, error: null });
    try {
      const credits = await creditApiClient.getMyCredits();
      set({ userCredits: credits, loading: false });
    } catch (error) {
      console.error("Error loading user credits:", error);
      set({
        error: "Failed to load credit balance",
        loading: false
      });
    }
  },

  loadTransactions: async (_userId: string) => {
    set({ loading: true, error: null });
    try {
      const transactions = await creditApiClient.getMyTransactions();
      set({ transactions, loading: false });
    } catch (error) {
      console.error("Error loading transactions:", error);
      set({
        error: "Failed to load transaction history",
        loading: false
      });
    }
  },

  loadBookings: async (_userId: string) => {
    set({ loading: true, error: null });
    try {
      const bookings = await bookingApiClient.getMyBookings({ status: BookingStatusFilterEnum.ALL });
      set({ bookings, loading: false });
    } catch (error) {
      console.error("Error loading bookings:", error);
      set({
        error: "Failed to load booking history",
        loading: false
      });
    }
  },

  bookDinner: async (data: CreateBooking) => {
    set({ loading: true, error: null });
    try {
      const booking = await bookingApiClient.book(data);
      return booking;
    } catch (error) {
      console.error("Error booking dinner:", error);
      set({
        error: "Failed to create booking",
        loading: false
      });
      return null;
    }
  },

  refreshCredits: async (_userId: string) => {
    try {
      const credits = await creditApiClient.getMyCredits();
      if (credits) {
        set({ userCredits: credits });
      }
    } catch (error) {
      console.error("Error refreshing credits:", error);
    }
  },

  setLoading: (loading: boolean) => {
    set({ loading });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  reset: () => {
    set(initialState);
  }
}));
